import { useEffect, useReducer, useState } from 'react';
import type { Editor } from '@tiptap/core';
import type { Node as PmNode } from '@tiptap/pm/model';
import { ChevronDown, ChevronRight, ListTodo } from 'lucide-react';
import { itemProgress, type ListKind } from '../editor/lists/NamedListNodes';

const MAX_QUICK_ADD = 2000;

interface PanelItem { itemId: string; text: string; checked: boolean; showBox: boolean; depth: number; progress: { done: number; total: number } | null }
interface PanelList { listId: string; name: string; kind: ListKind; items: PanelItem[]; open: number }

function readLists(editor: Editor): PanelList[] {
  const out: PanelList[] = [];
  editor.state.doc.descendants((n) => {
    if (n.type.name !== 'namedList') return true;
    const kind = n.attrs.kind as ListKind;
    const items: PanelItem[] = [];
    let open = 0;
    const walk = (node: PmNode, depth: number) => {
      node.forEach((c) => {
        if (c.type.name !== 'namedListItem') return;
        const top = depth === 0;
        if (top && kind === 'checklist' && !c.attrs.checked) open++;
        items.push({
          itemId: c.attrs.itemId,
          text: c.firstChild?.textContent ?? '',
          checked: !!c.attrs.checked,
          showBox: top ? kind === 'checklist' : !!c.attrs.checkbox,
          depth,
          progress: itemProgress(c),
        });
        walk(c, depth + 1);
      });
    };
    walk(n, 0);
    out.push({ listId: n.attrs.listId, name: n.attrs.name, kind, items, open });
    return false;
  });
  return out;
}

/**
 * The meeting's named lists in its right panel (#337). A second view of the
 * same document: it reads the open editor on every change, and ticking or
 * quick-adding here writes to the document, so the body and the panel can
 * never disagree. Collapses like Flags and Follow-ups (#334).
 */
export function MeetingListsSection({ editor }: { editor: Editor | null }) {
  const [, rerender] = useReducer((x: number) => x + 1, 0);
  const [isOpen, setIsOpen] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!editor) return;
    // Document changes only: a cursor move changes nothing here.
    editor.on('update', rerender);
    return () => { editor.off('update', rerender); };
  }, [editor]);

  if (!editor) return null;
  const lists = readLists(editor);
  const open = lists.reduce((n, l) => n + l.open, 0);

  const quickAdd = (listId: string) => {
    const text = (drafts[listId] ?? '').trim().slice(0, MAX_QUICK_ADD);
    if (!text) return;
    editor.commands.addListItem(listId, text, { select: false });
    setDrafts((d) => ({ ...d, [listId]: '' }));
  };

  return (
    <div className="px-3 py-2">
      <h3 className="m-0 mb-1 text-xs font-semibold uppercase tracking-wider text-text-secondary">
        <button type="button" aria-expanded={isOpen} onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 py-1 uppercase tracking-wider hover:text-text-primary transition-colors">
          {isOpen ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
          <ListTodo size={12} aria-hidden />Lists
          <span className="font-mono text-[11px] font-normal normal-case tracking-normal text-text-muted">{open} open</span>
        </button>
      </h3>
      {isOpen && lists.length === 0 && (
        <p className="text-xs text-text-muted">
          No lists yet.{' '}
          <button type="button" className="text-accent hover:text-accent-hover" aria-label="Insert a list"
            onClick={() => editor.chain().focus().insertNamedList().run()}>Insert a list</button>
          {' '}or type /list on an empty line.
        </p>
      )}
      {isOpen && lists.map((l) => {
        const done = l.items.filter((i) => i.depth === 0 && i.checked).length;
        const tops = l.items.filter((i) => i.depth === 0).length;
        return (
          <section key={l.listId} className="mb-2">
            <div className="flex items-center justify-between gap-2 text-sm font-medium text-text-primary">
              <span className="truncate">{l.name}</span>
              {l.kind === 'checklist' && tops > 0 && <span className="font-mono text-[11px] text-text-muted">{done}/{tops}</span>}
            </div>
            <ul className="m-0 list-none p-0">
              {l.items.map((i) => (
                <li key={i.itemId} className="flex items-start gap-1.5 py-0.5 text-sm" style={{ paddingLeft: `${i.depth * 14}px` }}>
                  {i.showBox ? (
                    <input type="checkbox" aria-label={i.text || 'Empty item'} checked={i.checked} className="mt-1"
                      onChange={() => editor.commands.toggleListItem(i.itemId)} />
                  ) : <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-text-muted" />}
                  <span className={`flex-1 break-words ${i.checked ? 'text-text-muted line-through' : 'text-text-secondary'}`}>{i.text}</span>
                  {i.progress && <span className="font-mono text-[11px] text-text-muted">{i.progress.done}/{i.progress.total}</span>}
                </li>
              ))}
            </ul>
            <input
              aria-label={`Add to ${l.name}`}
              placeholder="Add an item..."
              value={drafts[l.listId] ?? ''}
              onChange={(e) => setDrafts((d) => ({ ...d, [l.listId]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); quickAdd(l.listId); } }}
              className="mt-1 w-full rounded-md border border-border bg-bg-input px-2 py-1 text-xs text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
            />
          </section>
        );
      })}
    </div>
  );
}
